import { useEffect, useRef, useState } from "react";
import { loadGrid, loadGridPage, previewError } from "../api/reports";
import type { PreviewGrid, PreviewGridSection } from "../types";

const gridCache = new Map<string, PreviewGrid>();

export function useWorkbookGrid(artifactId: string, sheetIndex: number) {
    const [attempt, setAttempt] = useState(0);
    const baseKey = `${artifactId}/${sheetIndex}`;
    const key = `${baseKey}/${attempt}`;
    const [state, setState] = useState<{ key: string; grid: PreviewGrid | null; loading: boolean; error: string; }>
        ({ key: "", grid: null, loading: true, error: "" });
    const active = useRef<AbortController | null>(null);

    useEffect(() => {
        const cached = gridCache.get(baseKey);
        if (cached) {
            setState({ key, grid: cached, loading: false, error: "" });
            return;
        }
        const controller = new AbortController();
        active.current = controller;
        setState({ key, grid: null, loading: true, error: "" });
        const timer = window.setTimeout(async () => {
            if (controller.signal.aborted) return;
            try {
                const grid = await loadGrid(artifactId, sheetIndex, controller.signal);
                if (!controller.signal.aborted) {
                    gridCache.set(baseKey, grid);
                    if (gridCache.size > 16) gridCache.delete(gridCache.keys().next().value!);
                    setState({ key, grid, loading: false, error: "" });
                }
            } catch (e) {
                const error = await previewError(e);
                if (!controller.signal.aborted) setState({ key, grid: null, loading: false, error });
            }
        }, 0);
        return () => { window.clearTimeout(timer); controller.abort(); };
    }, [artifactId, sheetIndex, baseKey, key]);

    const current = state.key === key ? state : { grid: null, loading: true, error: "" };
    return { ...current, retry: () => setAttempt(n => n + 1),
        cancel: () => {
            gridCache.delete(baseKey);
            active.current?.abort();
            setState({ key, grid: null, loading: false, error: "Grid preview cancelled. You can retry or download Excel." });
        } };
}

const pageCache = new Map<string, PreviewGridSection>();
const pageSelections = new Map<string, { page: number; pageSize: number }>();

export function useWorkbookGridPage(artifactId: string, sheetIndex: number, sectionIndex: number, initial: PreviewGridSection) {
    const selectionKey = `${artifactId}/${sheetIndex}/${sectionIndex}`;
    const [page, setPage] = useState(pageSelections.get(selectionKey)?.page || 1);
    const [pageSize, setPageSize] = useState(pageSelections.get(selectionKey)?.pageSize || initial.pageSize || 50);
    const [attempt, setAttempt] = useState(0);
    const key = `${artifactId}/${sheetIndex}/${sectionIndex}/${page}/${pageSize}`;
    const [state, setState] = useState({ key: "", section: initial, loading: false, error: "" });
    useEffect(() => {
        if (!initial.isPaged) return;
        const cached = page === 1 && pageSize === (initial.pageSize || 50) ? initial : pageCache.get(key);
        if (cached) { setState({ key, section: cached, loading: false, error: "" }); return; }
        const controller = new AbortController();
        setState(previous => ({ ...previous, key, loading: true, error: "" }));
        const timer = window.setTimeout(async () => {
            try {
                const section = await loadGridPage(artifactId, sheetIndex, sectionIndex, page, pageSize, controller.signal);
                if (controller.signal.aborted) return;
                pageCache.set(key, section);
                if (pageCache.size > 32) pageCache.delete(pageCache.keys().next().value!);
                setState({ key, section, loading: false, error: "" });
            } catch (error) {
                const message = await previewError(error);
                if (!controller.signal.aborted) setState(previous => ({ ...previous, key, loading: false, error: message }));
            }
        }, 0);
        return () => { window.clearTimeout(timer); controller.abort(); };
    }, [artifactId, sheetIndex, sectionIndex, initial, key, page, pageSize, attempt]);
    return { section: initial.isPaged ? state.section : initial, loading: initial.isPaged && (state.loading || state.key !== key),
        error: state.error, page, pageSize, change: (nextPage: number, nextSize: number) => {
            const next = { page: nextSize !== pageSize ? 1 : nextPage, pageSize: nextSize };
            pageSelections.set(selectionKey, next);
            if (pageSelections.size > 64) pageSelections.delete(pageSelections.keys().next().value!);
            setPage(next.page); setPageSize(next.pageSize);
        }, retry: () => setAttempt(value => value + 1) };
}
